import { useState, useRef, useEffect } from "react";

/* ─────────────────────────────────────────────
   AQUA-GUARD SERVICE REPORT GENERATOR
   Client-facing PDF/print report after each visit
   ───────────────────────────────────────────── */

// ── LSI Calculator (same as dashboard) ──
function calculateLSI(pH, temp, calcium, alkalinity, tds, cya = 0) {
  const tempC = (temp - 32) * 5 / 9;
  const TF = Math.log10(Math.max(tempC + 25.3, 1)) * 2.5 - 1.85;
  const CF = Math.log10(Math.max(calcium, 1)) - 0.4;
  const correctedAlk = Math.max(alkalinity - (cya / 3), 1);
  const AF = Math.log10(correctedAlk);
  const tdsF = (Math.log10(Math.max(tds, 1)) - 1) / 10;
  return Math.round((pH + TF + CF + AF + tdsF - 12.3) * 100) / 100;
}

function getLSIStatus(lsi) {
  if (lsi >= 0.31) return { label: "Scale-Forming", color: "#7c3aed", emoji: "🟣" };
  if (lsi >= 0.0) return { label: "Ideal", color: "#16a34a", emoji: "🟢" };
  if (lsi >= -0.3) return { label: "Acceptable", color: "#ca8a04", emoji: "🟡" };
  return { label: "Aggressive", color: "#dc2626", emoji: "🔴" };
}

// ── Sample report data (in production, pulled from dashboard state) ──
const SAMPLE_REPORTS = [
  {
    client: "Willow Creek HOA",
    address: "1420 Willow Creek Dr, Barrington, IL",
    type: "Commercial",
    pool: "50m lap + rec pool",
    volume: "120,000 gal",
    surface: "Plaster",
    contact: "Karen Aldridge",
    date: "February 25, 2026",
    tech: "Matt",
    arrival: { pH: 7.3, alk: 88, ch: 330, cya: 34, temp: 55, tds: 1390 },
    departure: { pH: 7.4, alk: 95, ch: 340, cya: 35, temp: 58, tds: 1400 },
    actions: [
      "Tested all chemical levels on arrival",
      "Added 4 lbs sodium bicarbonate to raise alkalinity",
      "Added 2 lbs calcium chloride to raise calcium hardness",
      "Brushed walls and tile line",
      "Emptied skimmer baskets (light debris)",
      "Checked pump and filter pressure — operating normally at 12 PSI",
      "Backwashed filter (pressure was at 18 PSI before)",
    ],
    equipment: { pump: "Pentair IntelliFlo 3HP — Normal", heater: "Raypak 406A — Normal", filter: "Hayward SwimClear C5030 — Cleaned", salt: null },
    notes: "Filter replaced January 2026, running well. Gate code 4821. Pool chemistry stabilizing after cold week. All systems normal.",
    nextService: "March 4, 2026",
    recommendation: "Water balance is improving after alkalinity and calcium adjustments. LSI shifted from Acceptable to Ideal range. With incoming cold temperatures this week, we recommend monitoring calcium levels closely. No equipment concerns at this time. Pool is in excellent condition for the season.",
  },
  {
    client: "Lakewood Fitness Center",
    address: "2100 Randall Rd, Carpentersville, IL",
    type: "Commercial",
    pool: "Indoor 25yd + therapy pool",
    volume: "45,000 gal",
    surface: "Plaster",
    contact: "Steph Nowak",
    date: "February 25, 2026",
    tech: "Matt",
    arrival: { pH: 7.8, alk: 130, ch: 450, cya: 85, temp: 82, tds: 2800 },
    departure: { pH: 7.6, alk: 120, ch: 450, cya: 85, temp: 82, tds: 2800 },
    actions: [
      "Tested all chemical levels on arrival",
      "Added 1 gal muriatic acid to lower pH and alkalinity",
      "CYA at 85 ppm — too high, needs partial drain (scheduled for next visit)",
      "Cleaned tile line — chloramine staining noted",
      "Checked HVAC interaction with pool temp — stable at 82°F",
      "Inspected pump seals — no leaks detected",
    ],
    equipment: { pump: "Pentair IntelliFlo VSF — Normal", heater: "Pentair MasterTemp 400 — Normal", filter: "Pentair Clean & Clear 420 — Due for cleaning next visit", salt: null },
    notes: "CYA remains critically high at 85 ppm. Partial drain is scheduled for next Tuesday. Chloramine buildup is causing indoor air quality concerns. Recommend discussing UV/ozone supplemental sanitizer with facility management to address long-term.",
    nextService: "March 4, 2026",
    recommendation: "Primary concern is the high CYA level (85 ppm) which is reducing chlorine effectiveness. A partial drain of approximately 40% pool volume is scheduled for next visit to bring CYA into the 30-50 ppm range. pH was successfully lowered from 7.8 to 7.6. The facility should consider a UV or ozone supplemental system to reduce chloramine formation — this would improve both water quality and indoor air quality.",
  },
];

export default function ServiceReportGenerator() {
  const [selectedReport, setSelectedReport] = useState(0);
  const [liveReport, setLiveReport] = useState(null);
  const reportRef = useRef(null);

  useEffect(() => {
    const stored = localStorage.getItem('ag_report_data');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setLiveReport(parsed);
      } catch (e) {
        console.error('Failed to parse report data', e);
      }
    }
  }, []);

  const report = liveReport || SAMPLE_REPORTS[selectedReport];
  const arrivalLSI = calculateLSI(report.arrival.pH, report.arrival.temp, report.arrival.ch, report.arrival.alk, report.arrival.tds, report.arrival.cya);
  const departureLSI = calculateLSI(report.departure.pH, report.departure.temp, report.departure.ch, report.departure.alk, report.departure.tds, report.departure.cya);
  const arrivalStatus = getLSIStatus(arrivalLSI);
  const departureStatus = getLSIStatus(departureLSI);

  const handlePrint = () => { window.print(); };

  const readings = [
    { label: "pH", arrival: report.arrival.pH, departure: report.departure.pH, ideal: "7.2–7.6" },
    { label: "Alkalinity", arrival: report.arrival.alk, departure: report.departure.alk, ideal: "80–120 ppm", unit: " ppm" },
    { label: "Calcium Hardness", arrival: report.arrival.ch, departure: report.departure.ch, ideal: "200–400 ppm", unit: " ppm" },
    { label: "Cyanuric Acid", arrival: report.arrival.cya, departure: report.departure.cya, ideal: "30–50 ppm", unit: " ppm" },
    { label: "Temperature", arrival: report.arrival.temp, departure: report.departure.temp, ideal: "—", unit: "°F" },
    { label: "TDS", arrival: report.arrival.tds, departure: report.departure.tds, ideal: "< 3,000 ppm", unit: " ppm" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#f0f0ed", fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        @media print {
          .no-print { display: none !important; }
          body { background: white; }
          @page { margin: 0.5in; size: letter; }
        }
      `}</style>

      {/* Controls (hidden on print) */}
      <div className="no-print" style={{ padding: "16px 20px", background: "#fff", borderBottom: "1px solid #e0e0e0", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: "linear-gradient(135deg, #0066CC, #00AACC)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800 }}>AG</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Service Report Generator</div>
            <div style={{ fontSize: 11, color: "#888" }}>Select a report and print/download</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {liveReport ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", borderRadius: 8, background: "#F3EEFF", border: "1px solid #7C3AED", color: "#7C3AED", fontSize: 12, fontWeight: 600 }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: "#7C3AED" }} />
              Live Report from Mission Control
            </div>
          ) : (
            SAMPLE_REPORTS.map((r, i) => (
              <button key={i} onClick={() => setSelectedReport(i)} style={{
                padding: "8px 14px", borderRadius: 8, border: `1px solid ${selectedReport === i ? "#0066CC" : "#ddd"}`,
                background: selectedReport === i ? "#E8F2FF" : "#fff", fontSize: 12, fontWeight: 600,
                color: selectedReport === i ? "#0066CC" : "#666", cursor: "pointer", fontFamily: "inherit",
              }}>{r.client}</button>
            ))
          )}
          <button onClick={handlePrint} style={{
            padding: "8px 16px", borderRadius: 8, border: "none", background: "linear-gradient(135deg, #0066CC, #00AACC)",
            color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
          }}>🖨️ Print / Save PDF</button>
        </div>
      </div>

      {/* Report */}
      <div ref={reportRef} style={{ maxWidth: 780, margin: "24px auto", background: "#fff", borderRadius: 8, overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>

        {/* Header */}
        <div style={{ padding: "32px 40px 24px", borderBottom: "3px solid #0066CC" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#0066CC", textTransform: "uppercase", letterSpacing: 2, marginBottom: 4 }}>Aqua-Guard Management</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: "#111", letterSpacing: -0.5 }}>Service Report</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#111" }}>{report.date}</div>
              <div style={{ fontSize: 11, color: "#888" }}>Technician: {report.tech}</div>
            </div>
          </div>
        </div>

        {/* Client Info */}
        <div style={{ padding: "20px 40px", background: "#f9f9f7", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Client</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#111" }}>{report.client}</div>
            <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{report.address}</div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>Contact: {report.contact}</div>
          </div>
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Pool Details</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#111" }}>{report.pool}</div>
            <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{report.volume} · {report.surface} · {report.type}</div>
          </div>
        </div>

        {/* LSI Summary */}
        <div style={{ padding: "24px 40px", display: "flex", gap: 20 }}>
          <div style={{ flex: 1, padding: "16px 20px", borderRadius: 10, border: `2px solid ${arrivalStatus.color}22`, background: `${arrivalStatus.color}08` }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Arrival LSI</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 32, fontWeight: 800, color: arrivalStatus.color }}>{arrivalLSI.toFixed(2)}</div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: arrivalStatus.color }}>{arrivalStatus.emoji} {arrivalStatus.label}</div>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", fontSize: 20, color: "#ccc" }}>→</div>
          <div style={{ flex: 1, padding: "16px 20px", borderRadius: 10, border: `2px solid ${departureStatus.color}22`, background: `${departureStatus.color}08` }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Departure LSI</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 32, fontWeight: 800, color: departureStatus.color }}>{departureLSI.toFixed(2)}</div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: departureStatus.color }}>{departureStatus.emoji} {departureStatus.label}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Chemical Readings Table */}
        <div style={{ padding: "0 40px 24px" }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Chemical Readings</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #e0e0e0" }}>
                <th style={{ textAlign: "left", padding: "8px 12px", fontSize: 10, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: 0.5 }}>Parameter</th>
                <th style={{ textAlign: "center", padding: "8px 12px", fontSize: 10, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: 0.5 }}>Arrival</th>
                <th style={{ textAlign: "center", padding: "8px 12px", fontSize: 10, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: 0.5 }}>Departure</th>
                <th style={{ textAlign: "center", padding: "8px 12px", fontSize: 10, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: 0.5 }}>Ideal Range</th>
              </tr>
            </thead>
            <tbody>
              {readings.map((r, i) => {
                const improved = r.label === "pH" ? Math.abs(r.departure - 7.4) < Math.abs(r.arrival - 7.4) : r.departure >= r.arrival;
                return (
                  <tr key={i} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "10px 12px", fontWeight: 600, color: "#333" }}>{r.label}</td>
                    <td style={{ padding: "10px 12px", textAlign: "center", color: "#666" }}>{r.arrival}{r.unit || ""}</td>
                    <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: 600, color: "#111" }}>{r.departure}{r.unit || ""}</td>
                    <td style={{ padding: "10px 12px", textAlign: "center", color: "#999", fontSize: 11 }}>{r.ideal}</td>
                  </tr>
                );
              })}
              <tr style={{ borderTop: "2px solid #e0e0e0", background: "#f9f9f7" }}>
                <td style={{ padding: "10px 12px", fontWeight: 700, color: "#111" }}>LSI (Calculated)</td>
                <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: 700, color: arrivalStatus.color }}>{arrivalLSI.toFixed(2)}</td>
                <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: 700, color: departureStatus.color }}>{departureLSI.toFixed(2)}</td>
                <td style={{ padding: "10px 12px", textAlign: "center", color: "#999", fontSize: 11 }}>-0.30 to +0.30</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Service Actions */}
        <div style={{ padding: "0 40px 24px" }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Service Performed</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {report.actions.map((action, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span style={{ width: 18, height: 18, borderRadius: 4, background: "#E8F2FF", color: "#0066CC", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>✓</span>
                <span style={{ fontSize: 13, color: "#444", lineHeight: 1.5 }}>{action}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Equipment Status */}
        <div style={{ padding: "0 40px 24px" }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Equipment Status</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {Object.entries(report.equipment).filter(([k, v]) => v).map(([key, val], i) => {
              const isNormal = val.includes("Normal");
              return (
                <div key={i} style={{ padding: "10px 14px", borderRadius: 8, background: "#f9f9f7", border: `1px solid ${isNormal ? "#e8e8e4" : "#fde68a"}` }}>
                  <div style={{ fontSize: 10, color: "#999", textTransform: "capitalize", marginBottom: 2 }}>{key}</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: isNormal ? "#333" : "#b45309" }}>{val}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* AI Recommendation */}
        <div style={{ padding: "0 40px 24px" }}>
          <div style={{ padding: "18px 22px", borderRadius: 10, background: "linear-gradient(135deg, #f0f7ff, #f5f0ff)", border: "1px solid #d0e0f0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
              <span style={{ fontSize: 14 }}>🧠</span>
              <span style={{ fontSize: 9, fontWeight: 700, color: "#0066CC", textTransform: "uppercase", letterSpacing: 1 }}>Analysis & Recommendation</span>
            </div>
            <div style={{ fontSize: 13, color: "#444", lineHeight: 1.7 }}>{report.recommendation}</div>
          </div>
        </div>

        {/* Tech Notes */}
        {report.notes && (
          <div style={{ padding: "0 40px 24px" }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Technician Notes</div>
            <div style={{ fontSize: 12, color: "#666", lineHeight: 1.7, padding: "12px 16px", borderRadius: 8, background: "#f9f9f7", borderLeft: "3px solid #ddd" }}>{report.notes}</div>
          </div>
        )}

        {/* Next Service */}
        <div style={{ padding: "20px 40px", background: "#f9f9f7", borderTop: "1px solid #e8e8e4", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Next Scheduled Service</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#111" }}>{report.nextService}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Questions?</div>
            <div style={{ fontSize: 12, color: "#0066CC", fontWeight: 600 }}>info@aquaguardmanagement.com</div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 40px", background: "#111", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 24, height: 24, borderRadius: 6, background: "linear-gradient(135deg, #0066CC, #00AACC)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 800 }}>AG</div>
            <span style={{ fontSize: 11, fontWeight: 600, color: "#fff", letterSpacing: 0.5 }}>Aqua-Guard Management</span>
          </div>
          <div style={{ fontSize: 9, color: "#666" }}>aquaguardmanagement.com · Palatine, IL</div>
        </div>
      </div>

      {/* Info for non-print */}
      <div className="no-print" style={{ maxWidth: 780, margin: "16px auto 40px", padding: "16px 20px", background: "#fff", borderRadius: 8, border: "1px solid #e0e0e0" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#0066CC", marginBottom: 6 }}>How This Works in Production</div>
        <div style={{ fontSize: 12, color: "#666", lineHeight: 1.7 }}>
          After each service visit, the dashboard auto-generates this report using the client's readings, service actions logged by the tech, and an AI-generated recommendation. You can print it, save as PDF (Ctrl+P → Save as PDF), or email directly to the client. The AI recommendation section is generated by the same engine powering the dashboard chat — it analyzes the chemistry changes and provides specific, actionable advice.
        </div>
      </div>
    </div>
  );
}
